import React from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import CopyHelper from '../Copy'
import Link from '../Link'
import TokenLogo from '../TokenLogo'
import { TYPE } from '../../Theme'
import { ExplorerDataType, getExplorerLink } from '../../utils/etherscan'
import { usedChain } from '../../constants'

const CardsContainer = styled.div`
  display: flex;
  gap: 20px;
  align-items: center;
  width: 100%;
  margin-bottom: 32px;

  @media screen and (max-width: 1200px) {
    flex-wrap: wrap;
  }

  @media screen and (max-width: 768px) {
    flex-direction: column;
    gap: 16px;
  }
`

const InfoCard = styled.div`
  flex: 1;
  background: #1b1c21;
  border-radius: 16px;
  padding: 32px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-height: 132px;
  min-width: 0;

  @media screen and (max-width: 768px) {
    width: 100%;
  }
`

const CardLabel = styled.div`
  font-family: 'Inter Display', sans-serif;
  font-weight: 500;
  font-size: 14px;
  color: white;
  opacity: 0.5;
  line-height: 1.4;
`

const AddressContainer = styled.div`
  background: rgba(34, 35, 40, 0.8);
  backdrop-filter: blur(16px);
  border: 1px solid #222328;
  border-radius: 32px;
  padding: 12px 16px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`

const AddressWithToken = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const AddressText = styled.div`
  font-family: 'Inter Display', sans-serif;
  font-weight: 500;
  font-size: 14px;
  color: white;
  white-space: nowrap;
`

const PolygonscanButton = styled.div`
  background: #222328;
  border-radius: 32px;
  padding: 12px 16px;
  height: 40px;
  display: flex;
  align-items: center;
  cursor: pointer;
  text-decoration: none;

  &:hover {
    opacity: 0.8;
  }
`

const PairInformation = ({ token0, token1, pairAddress }) => {
  return token0 && token1 ? (
    <>
      <TYPE.header marginBottom="10px" fontWeight={700} fontSize={24} lineHeight="56px" marginTop="32px">
        Pair Information
      </TYPE.header>
      <CardsContainer>
        {/* Pair Name Card */}
        <InfoCard>
          <CardLabel>Pair Name</CardLabel>
          <div
            style={{
              fontFamily: 'Inter Display, sans-serif',
              fontWeight: 500,
              fontSize: '18px',
              color: 'white',
              lineHeight: '1.4',
              whiteSpace: 'nowrap',
              display: 'block',
            }}
          >
            {`${token0?.symbol || ''}-${token1?.symbol || ''}`}
          </div>
        </InfoCard>

        {/* Pair Address Card */}
        <InfoCard>
          <CardLabel>Pair Address</CardLabel>
          <AddressContainer>
            <AddressText>{pairAddress.slice(0, 6) + '...' + pairAddress.slice(38, 42)}</AddressText>
            <CopyHelper toCopy={pairAddress} />
          </AddressContainer>
        </InfoCard>

        {/* Token0 Address Card */}
        <InfoCard>
          <CardLabel style={{ whiteSpace: 'nowrap' }}>{token0?.symbol ?? ''} Address</CardLabel>
          <AddressContainer>
            <AddressWithToken>
              <TokenLogo address={token0?.id} size="24px" />
              <AddressText>{token0 && token0.id.slice(0, 6) + '...' + token0.id.slice(38, 42)}</AddressText>
            </AddressWithToken>
            <CopyHelper toCopy={token0?.id} />
          </AddressContainer>
        </InfoCard>

        {/* Token1 Address Card */}
        <InfoCard>
          <CardLabel style={{ whiteSpace: 'nowrap' }}>{token1?.symbol ?? ''} Address</CardLabel>
          <AddressContainer>
            <AddressWithToken>
              <TokenLogo address={token1?.id} size="24px" />
              <AddressText>{token1 && token1.id.slice(0, 6) + '...' + token1.id.slice(38, 42)}</AddressText>
            </AddressWithToken>
            <CopyHelper toCopy={token1?.id} />
          </AddressContainer>
        </InfoCard>

        {/* Polygonscan Card */}
        <InfoCard>
          <CardLabel>Polygonscan</CardLabel>
          <Link external href={getExplorerLink(usedChain, pairAddress, ExplorerDataType.ADDRESS)}>
            <PolygonscanButton>
              <AddressText>View on Polygonscan</AddressText>
            </PolygonscanButton>
          </Link>
        </InfoCard>
      </CardsContainer>
    </>
  ) : (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '400px',
        color: 'white',
        fontSize: '24px',
      }}
    >
      Loading...
    </div>
  )
}

PairInformation.propTypes = {
  token0: PropTypes.shape({
    symbol: PropTypes.string,
    id: PropTypes.string,
  }),
  token1: PropTypes.shape({
    symbol: PropTypes.string,
    id: PropTypes.string,
  }),
  pairAddress: PropTypes.string.isRequired,
}

PairInformation.defaultProps = {
  token0: null,
  token1: null,
}

export default PairInformation
